import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";

interface BarChartProps<T extends object> {
  title: string;
  data: T[];
  metric: string;
  accessor: (d: T) => number;
  sortAccessor?: (d: T) => number | null;
  topN?: number;
  hideOther?: boolean;
  categoryAccessor: (d: T) => string;
  height: number;
  colors: string[];
  colorByCategory?: boolean;
  type?: "bar" | "column";
  formatter?: (value: number) => string;
}

const BarChart = <T extends object>({
  title,
  data,
  metric,
  accessor,
  sortAccessor,
  topN = 5,
  hideOther = false,
  categoryAccessor,
  height,
  colors,
  colorByCategory = false,
  type = "bar",
  formatter,
}: BarChartProps<T>) => {
  const orderAccessor = sortAccessor || accessor;
  const orderedData = data.sort((a, b) => {
    const a1 = orderAccessor(a) || 0;
    const b1 = orderAccessor(b) || 0;
    // sort descending
    return b1 - a1;
  });
  const topData = orderedData.slice(0, topN);
  const otherData = orderedData.slice(topN).reduce((acc, d) => {
    return (accessor(d) || 0) + acc;
  }, 0);
  const hasOther = !hideOther && otherData > 0;
  const options: Highcharts.Options = {
    colors,
    title: { text: "" },
    chart: {
      type,
      height,
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
    },
    xAxis: {
      labels: {
        useHTML: true,
        allowOverlap: true,
        style:
          type === "bar" ?
            {
              wordBreak: "normal",
              textOverflow: "allow",
              width: 150,
              minWidth: "150px",
              textAlign: "right",
            }
          : {},
      },
      scrollbar: {
        enabled: true,
      },
      categories: [...topData.map(categoryAccessor), ...(hasOther ? ["Other"] : [])],
    },
    legend: { enabled: false },
    yAxis: {
      title: {
        text: metric,
      },
    },
    tooltip: {
      useHTML: true,
      backgroundColor: "#FFFFFF",
      formatter: function () {
        const container = "<div style='background-color: #FFFFFF;z-index: 9999;'>";
        let content = "";
        if (formatter) {
          content = `${this.x}: ${formatter(this.y!)}`;
        } else {
          content = `${this.x}: ${this.y}`;
        }
        return container + content + "</div>";
      },
    },
    plotOptions: {
      column: { colorByPoint: colorByCategory, colors: colorByCategory ? colors : undefined },
      bar: { colorByPoint: colorByCategory, colors: colorByCategory ? colors : undefined },
    },
    series: [
      {
        type,
        data: [...topData.map((d) => accessor(d) || 0), ...(hasOther ? [otherData] : [])],
      },
    ],
  };

  return (
    <div>
      <h5 className="text-center">{title}</h5>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

export default BarChart;
